import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import promoBillboardsService from '../../services/promoBillboardsService';
import { showMsg } from '../../services/library';
import {
  DEFAULT_DURATION,
  IMAGE_BILLBOARD,
  RESOLUTION_3x2,
  RESOLUTION_4x3
} from '../../constants/commonConst';

const REQ_PROMOBILLBOARDS = 'promobillboards/';

export const INITIAL_BILLBOARDS_FILTER = {
  id: 0,
  imgPosition: 0,
  New: false,
  Normal: false,
  VDS: false,
  Used: false,
  Prelim: false,
  CPO: false,
  photoOperator: null,
  photoValue: '',
  FromYear: null,
  ToYear: null,
  VIN: null,
  ExcludeMake: false,
  Make: null,
  ExcludeModel: false,
  Model: null,
  ExcludeTrim: false,
  Trim: null,
  MinPrice: '',
  MaxPrice: '',
  MinMileage: '',
  MaxMileage: '',
  ExcludeElectric: false,
  ageOperator: null,
  ageValue: '',
  stockOperator: null,
  stockValue: '',
  startDate: null,
  endDate: null,
  durationType: DEFAULT_DURATION,
  durationDays: '',
  added: false,
  notAdded: false,
  billboardId: null,
  billboardUrl: null,
  billboardType: null
};

export const INITIAL_BILLBOARD_OBJ = {
  pbb_type: IMAGE_BILLBOARD,
  pbb_fileurl: null,
  billboardFile: null,
  overlayContent: []
};

// Get templatelist and fontlist
export const fetchPromoBillboardsInfoReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}fetchBillboardDetails`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.getBillboardDetails(data);
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Add template
export const saveTemplateReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}saveTemplate`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.saveTemplate(data);
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors);
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error);
      return rejectWithValue(error);
    }
  }
);

// Update template
export const updateTemplateReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}updateTemplate`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.updateTemplate(data);
      if (response?.status) {
        return response.data[0] || null;
      } else {
        showMsg(response?.errors);
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error);
      return rejectWithValue(error);
    }
  }
);

// Delete template
export const deleteTemplateReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}deleteTemplate`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.deleteTemplate(data);
      if (response?.status) {
        return response || null;
      } else {
        showMsg(response?.errors);
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error);
      return rejectWithValue(error);
    }
  }
);

// Add/Update promo billboard filter
export const saveBillboardFilterReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}saveFilter`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.saveBillboardFilter(data);
      if (response?.status) {
        return response || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Delete promo billboard filter
export const deleteBillboardFilterReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}deleteBillboardFilter`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.deleteBillboardFilter(data);
      if (response?.status) {
        return { filterId: data?.filterId };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Reset process
export const resetBillboardProcessReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}resetBillboardProcess`,

  async (data, { rejectWithValue }) => {
    try {
      await promoBillboardsService.resetVinBillboardProcess(data);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Add billboard
export const addBillboardReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}addBillboard`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.addBillboard(data);
      return response;
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Add billboard
export const removeBillboardReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}removeBillboard`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.removeBillboard(data);
      return response;
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Get vehicle photos
export const getVehicleGalleryReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}getVehicleGallery`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.getVehicleGallery(data);
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// get billboard history
export const getBillboardHistoryReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}getBillboardHistory`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.getBillboardHistory(data);
      if (response?.status) {
        return response.data || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// Save Promo Billboard Filter Priorities
export const saveBillboardFilterPrioritiesReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}saveFilterPriorities`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.saveFilterPriorities(data);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { filterList: data?.filterList };
      } else {
        showMsg(response?.errors);
      }
    } catch (error) {
      showMsg(error);
      return rejectWithValue(error);
    }
  }
);

export const updateFilterNameReq = createAsyncThunk(
  `${REQ_PROMOBILLBOARDS}updateFilterName`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await promoBillboardsService.updateFilterName(data);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { data };
      } else {
        showMsg(response?.errors);
      }
    } catch (error) {
      showMsg(error);
      return rejectWithValue(error);
    }
  }
);

const billboardsSlice = createSlice({
  name: 'PromoBillboards',
  initialState: {
    activeTab: 'library',
    billboardRatio: { is43: false, resolution: RESOLUTION_3x2 },

    // Billboard library
    billboardDetails: INITIAL_BILLBOARD_OBJ,
    activeBillboardType: null,
    promoBillboardList: [],
    selectedTemplate: null,
    tempName: '',
    fontList: [],
    isFontLoading: true,
    fontLibraryToogle: false,
    defaultImages: [],
    defaultImagesToogle: false,
    previewToggle: false,
    previewUrl: null,

    // Add billboard
    viewType: 'top',
    vin: '',
    selectedFilter: INITIAL_BILLBOARDS_FILTER,
    vehLoaded: false,
    invVehicles: [],
    filterTagList: [],
    vinList: [],
    yearList: [],
    makeList: [],
    modelList: [],
    trimList: [],
    filterModalToggle: false,
    filterName: '',
    filterList: [],
    billboardProgress: { status: false, value: 0 },

    // Billboard history
    historyLoaded: false,
    billboardHistorydetails: [],
    historyStart: moment().add(-15, 'days').toDate(),
    historyEnd: moment().toDate()
  },

  reducers: {
    handleActiveTab: {
      reducer(state, action) {
        state.activeTab = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },

    // Billboard library
    handleBillboardDetails: {
      reducer(state, action) {
        state.billboardDetails = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleActiveBillboardType: {
      reducer(state, action) {
        state.activeBillboardType = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    // handlePromoBillboardList: {
    //   reducer(state, action) {
    //     state.promoBillboardList = action.payload;
    //   },
    //   prepare(e) {
    //     return { payload: e };
    //   }
    // },
    handleSelectedTemplate: {
      reducer(state, action) {
        state.selectedTemplate = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontList: {
      reducer(state, action) {
        state.fontList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },

    handleIsFontLoading: {
      reducer(state, action) {
        state.isFontLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontLibraryToogle: {
      reducer(state, action) {
        state.fontLibraryToogle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDefaultImages: {
      reducer(state, action) {
        state.defaultImages = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDefaultImagesToogle: {
      reducer(state, action) {
        state.defaultImagesToogle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePreviewToggle: {
      reducer(state, action) {
        state.previewToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePreviewUrl: {
      reducer(state, action) {
        state.previewUrl = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTempName: {
      reducer(state, action) {
        state.tempName = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleNameModalToggle: {
      reducer(state, action) {
        state.nameModalToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },

    // Add billboard
    handleViewType: {
      reducer(state, action) {
        state.viewType = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVin: {
      reducer(state, action) {
        state.vin = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSelectedFilter: {
      reducer(state, action) {
        state.selectedFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVehLoaded: {
      reducer(state, action) {
        state.vehLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleInvVehicles: {
      reducer(state, action) {
        state.invVehicles = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilterTagList: {
      reducer(state, action) {
        state.filterTagList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVinList: {
      reducer(state, action) {
        state.vinList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleYearList: {
      reducer(state, action) {
        state.yearList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleMakeList: {
      reducer(state, action) {
        state.makeList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleModelList: {
      reducer(state, action) {
        state.modelList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTrimList: {
      reducer(state, action) {
        state.trimList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilterModalToggle: {
      reducer(state, action) {
        state.filterModalToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilterName: {
      reducer(state, action) {
        state.filterName = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilterList: {
      reducer(state, action) {
        state.filterList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBillboardProgress: {
      reducer(state, action) {
        state.billboardProgress = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleHistoryLoaded: {
      reducer(state, action) {
        state.historyLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBillboardHistorydetails: {
      reducer(state, action) {
        state.billboardHistorydetails = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleHistoryStart: {
      reducer(state, action) {
        state.historyStart = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleHistoryEnd: {
      reducer(state, action) {
        state.historyEnd = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    }
  },

  extraReducers: builder => {
    builder
      // #region fetchPromoBillboardsInfoReq
      .addCase(fetchPromoBillboardsInfoReq.fulfilled, (state, action) => {
        const {
          dealerIs43,
          promoBillboardDetails,
          fontFileList,
          invVehiclesData,
          promoBillboardfilters,
          defaultImages
        } = action.payload;

        state.billboardRatio = { is43: dealerIs43, resolution: dealerIs43 ? RESOLUTION_4x3 : RESOLUTION_3x2 };
        const promoBillboardList =
          promoBillboardDetails?.map(d => ({
            ...d,
            label: d.pbb_name,
            value: d.pbb_id
          })) || [];

        // Library
        state.promoBillboardList = promoBillboardList;
        state.isFontLoading = false;

        state.fontList =
          fontFileList?.map(d => ({
            ...d,
            label: d.vf_filename,
            value: d.vf_id
          })) || [];

        // Add billboard
        const detail = invVehiclesData
          // .filter(y => y.veh_id > 0)
          .map(z => ({
            ...z,
            status: z?.pbl_processing_status || false,
            billboardStatus: z?.pbl_processing_status || (z?.pbl_startdate && z?.pbl_enddate) ? 1 : 0,
            filtered: true,
            checked: false,
            isCollapsed: false
          }));
        state.invVehicles = detail;

        const createList = key =>
          [...new Set(invVehiclesData.map(d => d[key]))]
            .filter(Boolean)
            .sort((a, b) => a - b)
            .map(value => ({ value, label: value }));

        state.vinList = createList('inv_vin');
        state.yearList = createList('inv_year');
        state.makeList = createList('inv_make');
        state.modelList = createList('inv_model');
        state.trimList = createList('inv_trim');

        state.defaultImages = defaultImages;

        const filters = promoBillboardfilters.map(x => ({
          ...x,
          value: x.pbf_id,
          label: x.pbf_name
        }));
        state.filterList = filters;
      })
      .addCase(fetchPromoBillboardsInfoReq.rejected, state => {
        state.isFontLoading = false;
      })
      // #endregion
      // #region saveTemplateReq
      .addCase(saveTemplateReq.fulfilled, (state, action) => {
        const { tempObject, billboardDetails } = action.payload;
        tempObject.value = tempObject.pbb_id;
        tempObject.label = tempObject.pbb_name;
        tempObject.overlayArr = billboardDetails;
        const updatedList = [...state.promoBillboardList, tempObject];
        state.promoBillboardList = updatedList;
        state.tempName = '';
        state.selectedTemplate = tempObject || null;
        showMsg('Billboard added successfully', 'success');
      })
      // #endregion
      // #region updateTemplateReq
      .addCase(updateTemplateReq.fulfilled, (state, action) => {
        const { billboard, billboardDetails, is43 = false } = action.payload;
        state.selectedFilter = {
          ...state.selectedFilter,
          billboardUrl:
            state.selectedFilter.billboardUrl && state.selectedFilter?.billboardId
              ? `${billboard?.pbb_billboardurl}?ver=${Date.now()}`
              : null,
          billboardType: state.selectedFilter ? billboard?.pbb_type : null
        };
        const promobillboardDetails = state?.promoBillboardList?.map(d =>
          d.pbb_id === state?.selectedTemplate?.value
            ? {
                ...d,
                ...billboard,
                billboardFile: null,
                overlayArr: billboardDetails,
                pbb_is43: d.pbb_id === state?.selectedTemplate?.value ? is43 : d.pbb_is43
              }
            : d
        );
        state.selectedTemplate = promobillboardDetails.find(d => d.pbb_id === state.selectedTemplate?.value);
        state.promoBillboardList = promobillboardDetails;
        showMsg('Billboard updated successfully', 'success');
      })
      // #endregion
      // #region deleteTemplateReq
      .addCase(deleteTemplateReq.fulfilled, (state, action) => {
        const { message } = action.payload;
        state.promoBillboardList = state?.promoBillboardList.filter(
          d => d.value !== state.selectedTemplate?.value
        );
        if (state.selectedFilter.billboardId === state.selectedTemplate?.value) {
          state.selectedFilter = { ...state.selectedFilter, billboardId: 0, billboardUrl: null };
        }
        state.selectedTemplate = null;
        showMsg(message, 'success');
      })
      // #endregion
      // #region deleteFilterReq
      .addCase(deleteBillboardFilterReq.fulfilled, (state, action) => {
        const { filterId } = action.payload;
        state.filterList = state?.filterList
          .filter(d => d.value !== filterId)
          .map((x, index) => ({
            ...x,
            pbf_order: index + 1
          }));
        state.selectedFilter = INITIAL_BILLBOARDS_FILTER;
        showMsg('Billboard filter deleted successfully', 'success');
      })
      // #region getBillboardHistoryReq
      .addCase(getBillboardHistoryReq.pending, state => {
        state.historyLoaded = true;
        state.billboardHistorydetails = [];
      })
      .addCase(getBillboardHistoryReq.fulfilled, (state, action) => {
        state.billboardHistorydetails = action.payload;
      })
      .addCase(getBillboardHistoryReq.rejected, state => {
        state.historyLoaded = true;
      })
      // #endregion
      // #region deleteFilterReq
      .addCase(saveBillboardFilterPrioritiesReq.fulfilled, (state, action) => {
        const { filterList } = action.payload;
        state.filterList = filterList.map(x => ({ ...x, pbf_order: x.order }));
      })
      // #endregion
      // #region updateFilterNameReq
      .addCase(updateFilterNameReq.fulfilled, (state, action) => {
        const data = action.payload?.data;
        if (data) {
          state.filterList = state.filterList.map(x => ({
            ...x,
            pbf_name: x.pbf_id === data.filterId ? data.filterName : x.pbf_name,
            label: x.pbf_id === data.filterId ? data.filterName : x.label
          }));
        }
      });
    // #endregion
  }
});

export const {
  handleActiveTab,

  // Billboard library
  handleBillboardDetails,
  handleActiveBillboardType,
  handlePromoBillboardList,
  handleSelectedTemplate,
  handleTempName,
  handleNameModalToggle,
  handleFontList,
  handleIsFontLoading,
  handleFontLibraryToogle,
  handleDefaultImages,
  handleDefaultImagesToogle,
  handlePreviewToggle,
  handlePreviewUrl,

  // Add billboard
  handleViewType,
  handleVin,
  handleSelectedFilter,
  handleVehLoaded,
  handleInvVehicles,
  handleFilterTagList,
  handleVinList,
  handleYearList,
  handleMakeList,
  handleModelList,
  handleTrimList,
  handleFilterModalToggle,
  handleFilterName,
  handleFilterList,
  handleBillboardProgress,

  // Billboard history
  handleHistoryLoaded,
  handleBillboardHistorydetails,
  handleHistoryStart,
  handleHistoryEnd
} = billboardsSlice.actions;

export default billboardsSlice.reducer;
